import { queryOptions, useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { makeMaxResourceQueryFn } from '#util/api.ts';
import { networkQueryKeys, Networks } from './index.ts';

/**
 * Query options for the networks list.
 */
export const networksQueryOptions = queryOptions({
  queryKey: networkQueryKeys.list(),
  queryFn: ({ signal }) => Networks.getNetworks({ init: { signal } }),
});

export const makeNetworkQueryOptions = (networkId: string) =>
  queryOptions({
    queryKey: networkQueryKeys.detail(networkId),
    queryFn: ({ signal }) => Networks.getNetwork(networkId, { signal }),
  });

/**
 * Max-pages query options for the networks list, paginating through up to 10 pages.
 */
export const maxNetworksQueryOptions = queryOptions({
  queryKey: networkQueryKeys.maxList,
  queryFn: makeMaxResourceQueryFn(Networks.getNetworks),
});

export function useSuspenseNetworkQuery(networkId: string) {
  return useSuspenseQuery(makeNetworkQueryOptions(networkId));
}

export const makeNetworkConfigOverridesQueryOptions = (networkId: string) =>
  queryOptions({
    queryKey: [...networkQueryKeys.detail(networkId), 'config-overrides'] as const,
    queryFn: ({ signal }) => Networks.getNetworkConfigOverrides(networkId, { signal }),
  });

/**
 * {@link useSuspenseNetworkConfigOverridesQueries} fetches config overrides for the given networks.
 */
export function useSuspenseNetworkConfigOverridesQueries(networkIds: string[]) {
  return useSuspenseQueries({
    queries: networkIds.map((networkId) => makeNetworkConfigOverridesQueryOptions(networkId)),
  });
}

/**
 * {@link networkCountQueryOptions} returns the total number of networks in the account.
 * Keyed under `lists()` so list invalidations also refresh the count.
 */
export const networkCountQueryOptions = queryOptions({
  queryKey: [...networkQueryKeys.lists(), 'count'] as const,
  queryFn: async ({ signal }) => (await Networks.getNetworks({ pageSize: 1, init: { signal } })).metadata.totalCount,
});

/**
 * {@link useSuspenseNetworkCount} returns the total number of networks for the current account.
 */
export function useSuspenseNetworkCount() {
  return useSuspenseQuery(networkCountQueryOptions);
}
